import { useState } from "react";
import {
  AppBar, Toolbar, IconButton, Typography, Box,
  Drawer, ListItemText, Button, ListItemButton,
  Menu, MenuItem, useTheme, useMediaQuery, Stack, Divider, Tooltip
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LanguageIcon from "@mui/icons-material/Language";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import CommentIcon from "@mui/icons-material/Comment";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import { useTranslation } from "react-i18next";
import supabase from "../../utils/supabase";
import { useSupabaseSession } from "../../utils/AuthSessionProvider";
import { useNavigate } from "react-router-dom";
import LogoutButton from "../LogoutButton/LogoutButton";
import LoginButton from "../Login/LoginButton/LoginButton";
import AdminChatBox from "../../components/ProtectedRoute/Admin/Dialogs/AdminChatDialog";
import UserInfoEditDialog from "../UserInfoEditDialog";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useSupabaseSession();
  const [anchorEl, setAnchorEl] = useState(null);
  const [taxonomyMenuAnchorEl, setTaxonomyMenuAnchorEl] = useState(null);
  const [analysisMenuAnchorEl, setAnalysisMenuAnchorEl] = useState(null);
  const [adminTeamMenuAnchorEl, setAdminTeamMenuAnchorEl] = useState(null);
  const [langMenuAnchorEl, setLangMenuAnchorEl] = useState(null);
  const [openUserInfoEditor, setOpenUserInfoEditor] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const { t, i18n } = useTranslation(['common', 'analysis']);

  const baseNavItems = [
    { label: t('home'), path: "/" },
    // { label: "Database", path: "/database" },
  ];

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
    handleCloseAdminMenu();
    handleCloseAnalysisMenu();
    handleCloseTaxonomyMenu();
    handleCloseAdminTeamMenu();
  };
  const handleOpenAdminMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseAdminMenu = () => setAnchorEl(null);

  const handleOpenAnalysisMenu = (event) => {
    setAnalysisMenuAnchorEl(event.currentTarget);
  };
  const handleCloseAnalysisMenu = () => setAnalysisMenuAnchorEl(null);
  
  const handleOpenTaxonomyMenu = (event) => {
    setTaxonomyMenuAnchorEl(event.currentTarget);
  };
  const handleCloseTaxonomyMenu = () => setTaxonomyMenuAnchorEl(null);

  const handleOpenAdminTeamMenu = (event) => {
    setAdminTeamMenuAnchorEl(event.currentTarget);
  };
  const handleCloseAdminTeamMenu = () => {
    setAdminTeamMenuAnchorEl(null);
  };

  const handleOpenLangMenu = (event) => {
    setLangMenuAnchorEl(event.currentTarget);
  };
  const handleCloseLangMenu = () => setLangMenuAnchorEl(null);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    handleCloseLangMenu();
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center", bgcolor: "#121212", height: "100%" }}>
      <Typography
        variant="h6"
        sx={{ my: 2, color: "#7FFFD4", cursor: "pointer" }}
        onClick={() => navigate("/")}
      >
        WIC
      </Typography>
      {baseNavItems.map(item => (
        <ListItemButton key={item.label} onClick={() => handleNavigate(item.path)} sx={{ color: "#7FFFD4" }}>
          <ListItemText primary={item.label} />
        </ListItemButton>
      ))}
      {/* Taxonomy Menu for Mobile */}
      <ListItemButton onClick={() => handleNavigate('/taxonomy')} sx={{ color: "#7FFFD4" }}>
        <ListItemText primary={t('taxonomy')} />
      </ListItemButton>
      <ListItemButton onClick={() => handleNavigate('/taxonomy/taxon-explore')} sx={{ color: "#7FFFD4", pl: 4 }}>
        <ListItemText primary={t('taxonExplorer')} />
      </ListItemButton>
      {/* Analysis Menu for Mobile */}
      <ListItemButton onClick={() => handleNavigate('/analysis')} sx={{ color: "#7FFFD4" }}>
        <ListItemText primary={t('analysis')} />
      </ListItemButton>
      <ListItemButton onClick={() => handleNavigate('/analysis/total-species-estimation')} sx={{ color: "#7FFFD4", pl: 4 }}>
        <ListItemText primary={t('estimationPage.title', { ns: 'analysis' })} />
      </ListItemButton>
      <ListItemButton onClick={() => handleNavigate('/analysis/comparison-with-checklists')} sx={{ color: "#7FFFD4", pl: 4 }}>
        <ListItemText primary={t('comparison.title', { ns: 'analysis' })} />
      </ListItemButton>
      <ListItemButton onClick={() => handleNavigate('/analysis/cumulative-description-trends')} sx={{ color: "#7FFFD4", pl: 4 }}>
        <ListItemText primary={t('cumulativeTrends.title', { ns: 'analysis' })} />
      </ListItemButton>
      <ListItemButton onClick={() => handleNavigate('/analysis/annual-description-trends')} sx={{ color: "#7FFFD4", pl: 4 }}>
        <ListItemText primary={t('annualTrends.title', { ns: 'analysis' })} />
      </ListItemButton>
      <ListItemButton key="admin-team" onClick={() => handleNavigate("/admin-team")} sx={{ color: "#7FFFD4" }}>
        <ListItemText primary={t('adminTeam')} />
      </ListItemButton>
      {user ? (
        <>
          <ListItemButton onClick={() => handleNavigate("/admin")} sx={{ color: "#7FFFD4" }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            <ListItemText primary="Dashboard" />
          </ListItemButton>
          <ListItemButton onClick={() => handleNavigate("/admin/console-scientific-names")} sx={{ color: "#7FFFD4", pl: 4 }}>
            <ListItemText primary="Scientific Names" />
          </ListItemButton>
          <ListItemButton onClick={() => handleNavigate("/admin/console-publications")} sx={{ color: "#7FFFD4", pl: 4 }}>
            <ListItemText primary="Publications" />
          </ListItemButton>
          <ListItemButton onClick={() => handleNavigate("/admin/console-journals")} sx={{ color: "#7FFFD4", pl: 4 }}>
            <ListItemText primary="Journals Console" />
          </ListItemButton>
          <ListItemButton onClick={() => handleNavigate("/admin/console-countries")} sx={{ color: "#7FFFD4", pl: 4 }}>
            <ListItemText primary="Countries Data Console" />
          </ListItemButton>
          <ListItemButton onClick={() => handleNavigate("/admin/console-comments")} sx={{ color: "#7FFFD4", pl: 4 }}>
            <ListItemText primary="Contributor Comments" />
          </ListItemButton>
          <ListItemButton onClick={() => handleNavigate('/admin/messages')} sx={{ color: "#7FFFD4", pl: 4 }}>
            <ListItemText primary={t('contactMessages')} />
          </ListItemButton>
          <Box sx={{ px: 2, mt: 2 }}>
            <LogoutButton />
          </Box>
        </>
      ) : (
        <LoginButton />
      )}
    </Box>
  );

  return (
    <>
      <AppBar position="fixed" sx={{ bgcolor: "#121212", color: "#7FFFD4", width: "100%" }}>
        <Toolbar>
          {isMobile && (
            <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant="h6"
            sx={{ flexGrow: 1, cursor: "pointer" }}
            onClick={() => navigate("/")}
          >
            WIC
          </Typography>

          {!isMobile && (
            <Stack direction="row" spacing={2} alignItems="center">
              {baseNavItems.map(item => (
                <Button
                  key={item.label}
                  onClick={() => handleNavigate(item.path)}
                  sx={{ color: "#7FFFD4", textTransform: "none" }}
                >
                  {item.label}
                </Button>
              ))}
              <Button
                onClick={handleOpenTaxonomyMenu}
                endIcon={<ExpandMoreIcon />}
                sx={{ color: "#7FFFD4", textTransform: "none" }}
              >
                {t('taxonomy')}
              </Button>
              <Menu
                anchorEl={taxonomyMenuAnchorEl}
                open={Boolean(taxonomyMenuAnchorEl)}
                onClose={handleCloseTaxonomyMenu}
                MenuListProps={{ sx: { bgcolor: "#1e1e1e", color: "#7FFFD4" } }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <MenuItem onClick={() => handleNavigate('/taxonomy')}>{t('taxonomyHome')}</MenuItem>
                <MenuItem onClick={() => handleNavigate('/taxonomy/taxon-explore')}>{t('taxonExplorer')}</MenuItem>
              </Menu>
              <Button
                onClick={handleOpenAnalysisMenu}
                endIcon={<ExpandMoreIcon />}
                sx={{ color: "#7FFFD4", textTransform: "none" }}
              >
                {t('analysis')}
              </Button>
              <Menu
                anchorEl={analysisMenuAnchorEl}
                open={Boolean(analysisMenuAnchorEl)}
                onClose={handleCloseAnalysisMenu}
                MenuListProps={{ sx: { bgcolor: "#1e1e1e", color: "#7FFFD4" } }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <MenuItem onClick={() => handleNavigate('/analysis')}>{t('analysisHome')}</MenuItem>
                <MenuItem onClick={() => handleNavigate('/analysis/total-species-estimation')}>{t('estimationPage.title', { ns: 'analysis' })}</MenuItem>
                <MenuItem onClick={() => handleNavigate('/analysis/comparison-with-checklists')}>{t('comparison.title', { ns: 'analysis' })}</MenuItem>
                <MenuItem onClick={() => handleNavigate('/analysis/cumulative-description-trends')}>{t('cumulativeTrends.title', { ns: 'analysis' })}</MenuItem>
                <MenuItem onClick={() => handleNavigate('/analysis/annual-description-trends')}>{t('annualTrends.title', { ns: 'analysis' })}</MenuItem>
              </Menu>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  onClick={handleOpenAdminTeamMenu}
                  endIcon={<ExpandMoreIcon />}
                  sx={{ color: "#7FFFD4", textTransform: "none" }}
                >
                  {t('adminTeam')}
                </Button>
                <Tooltip title={t('contactTooltip', 'Contact / Provide Information')}>
                  <IconButton onClick={() => handleNavigate('/admin-team/contact')} size="small" sx={{ ml: 0.5 }}>
                    <MailOutlineIcon sx={{ color: "#7FFFD4" }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Menu
                anchorEl={adminTeamMenuAnchorEl}
                open={Boolean(adminTeamMenuAnchorEl)}
                onClose={handleCloseAdminTeamMenu}
                MenuListProps={{ sx: { bgcolor: "#1e1e1e", color: "#7FFFD4" } }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <MenuItem onClick={() => handleNavigate('/admin-team')}>{t('adminTeamHome', 'Admin Team Home')}</MenuItem>
                <MenuItem onClick={() => handleNavigate('/admin-team/members')}>{t('memberList', 'Member List')}</MenuItem>
                <MenuItem onClick={() => handleNavigate('/admin-team/member-contribution')}>{t('memberContribution', 'Member Contribution')}</MenuItem>
                <MenuItem onClick={() => handleNavigate('/admin-team/contact')}>{t('contact', 'Contact')}</MenuItem>
              </Menu>

              <Tooltip title={t('changeLanguage')}>
                <IconButton onClick={handleOpenLangMenu} color="inherit">
                  <LanguageIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={langMenuAnchorEl}
                open={Boolean(langMenuAnchorEl)}
                onClose={handleCloseLangMenu}
                MenuListProps={{ sx: { bgcolor: "#1e1e1e", color: "#7FFFD4" } }}
              >
                <MenuItem onClick={() => changeLanguage('en')}>English</MenuItem>
                <MenuItem onClick={() => changeLanguage('ja')}>日本語</MenuItem>
                <MenuItem onClick={() => changeLanguage('zh')}>中文</MenuItem>
              </Menu>

              {user ? (
                <>
                  <Divider orientation="vertical" flexItem sx={{ bgcolor: "#7FFFD4", mx: 2 }} />
                  <Button
                    onClick={handleOpenAdminMenu}
                    endIcon={<ExpandMoreIcon />}
                    sx={{ color: "#7FFFD4", textTransform: "none" }}
                  >
                    DASHBOARD
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseAdminMenu}
                    MenuListProps={{
                      sx: { bgcolor: "#1e1e1e", color: "#7FFFD4" }
                    }}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                  >
                    {/* ダッシュボードホーム */}
                    <MenuItem onClick={() => handleNavigate("/admin")}>Home</MenuItem>

                    {/* コアコンソール群 */}
                    <Divider sx={{ my: 0.5, borderColor: 'rgba(127, 255, 212, 0.2)' }} />
                    <Typography variant="body1" sx={{ color: "#7FFFD4" }}>Core Consoles</Typography>
                    <MenuItem onClick={() => handleNavigate("/admin/console-scientific-names")}>Scientific Names</MenuItem>
                    <MenuItem onClick={() => handleNavigate("/admin/console-publications")}>Publications</MenuItem>
                    <MenuItem onClick={() => handleNavigate("/admin/console-journals")}>Journals</MenuItem>
                    
                    {/* コアコンソール群 */}
                    <Divider sx={{ my: 0.5, borderColor: 'rgba(127, 255, 212, 0.2)' }} />
                    <Typography variant="body1" sx={{ color: "#7FFFD4" }}>Optional Consoles</Typography>
                    <MenuItem onClick={() => handleNavigate("/admin/console-taxonomic-acts")}>Taxonomic Acts</MenuItem>
                    <MenuItem onClick={() => handleNavigate("/admin/console-bionomics")}>Bionomics</MenuItem>

                    {/* コアコンソール群 */}
                    <Divider sx={{ my: 0.5, borderColor: 'rgba(127, 255, 212, 0.2)' }} />
                    <Typography variant="body1" sx={{ color: "#7FFFD4" }}>Other Consoles</Typography>
                    <MenuItem onClick={() => handleNavigate("/admin/console-countries")}>Countries</MenuItem>
                    <MenuItem onClick={() => handleNavigate("/admin/console-repositories")}>Repositories</MenuItem>
                    <MenuItem onClick={() => handleNavigate("/admin/console-researchers")}>Researchers</MenuItem>
                    <MenuItem onClick={() => handleNavigate("/admin/console-fossils")}>Fossil Data</MenuItem>

                    <Divider sx={{ my: 0.5, borderColor: 'rgba(127, 255, 212, 0.2)' }} />
                    <MenuItem onClick={() => handleNavigate("/admin/messages")}>{t('contactMessages')}</MenuItem>
                    <MenuItem onClick={() => handleNavigate("/admin/console-comments")}>Contributor Comments</MenuItem>
                    <MenuItem onClick={() => handleNavigate("/admin/comparison-sync")}>Sync Checklist Stats</MenuItem>
                    
                  </Menu>

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2 }}>
                    <Typography variant="body1" sx={{ color: "#7FFFD4" }}>
                      {user.user_metadata?.display_name || user.email}
                    </Typography>

                    <Tooltip title={t('editProfile', 'Edit Your Profile')}>
                      <IconButton onClick={() => setOpenUserInfoEditor(true)} size="small">
                        <EditIcon sx={{ color: "#7FFFD4" }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Manage Comments">
                      <IconButton onClick={() => handleNavigate("/admin/console-comments")} size="small">
                        <CommentIcon sx={{ color: "#7FFFD4" }} />
                      </IconButton>
                    </Tooltip>

                    
                    <Tooltip title="Logout">
                      <IconButton onClick={handleLogout} size="small">
                        <LogoutIcon sx={{ color: "#7FFFD4" }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </>
              ) : (
                <LoginButton />
              )}
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      {/* モバイルDrawer */}
      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: 240,
              bgcolor: "#121212",
              color: "#7FFFD4"
            }
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* 上に隙間確保 */}
      <Toolbar />

      {/* ユーザー情報編集ダイアログ */}
      {user && (
        <UserInfoEditDialog
          open={openUserInfoEditor}
          onClose={() => setOpenUserInfoEditor(false)}
          user={user}
        />
      )}

      {/* ✅ チャットボックスは下に常に存在させる */}
      {user && (
        <AdminChatBox currentUserName={user.user_metadata?.display_name || user.email} />
      )}
    </>
  );
}
